import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getUserDetails } from '../../utils';
import { useQueryGetGoalsList } from '../../hooks/react-query/goalsManagement';

// ----------------------------------------------------------------------

export default function GoalsDetails() {
  const navigate = useNavigate();
  const theme = useTheme();
  const userDetails = getUserDetails();
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const isAdmin = userDetails?.role?.toUpperCase() === 'ADMIN';
  const rawSchoolIds = Array.isArray(userDetails?.schoolId)
    ? userDetails.schoolId.filter(Boolean).map(id => (typeof id === 'object' ? id._id || id.toString() : id))
    : [];
  const schoolId = isAdmin ? '' : (rawSchoolIds[0] || '');

  // Fetch goals using React Query
  const { data: goals = [], isLoading } = useQueryGetGoalsList(
    userDetails?.token,
    navigate,
    schoolId
  );

  React.useEffect(() => {
    let filtered = goals || [];

    // Search filter
    if (searchValue) {
      filtered = filtered.filter(
        (goal) =>
          goal.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
          goal.goalsFor?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          goal.goalsFrom?.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter === 'completed') {
      filtered = filtered.filter((goal) => goal.isCompleted);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter((goal) => !goal.isCompleted);
    }

    setFilteredGoals(filtered);
  }, [searchValue, statusFilter, goals]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <Helmet>
        <title>Goals Details | Edifying Teachers</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              background: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { background: alpha(theme.palette.primary.main, 0.2) },
            }}
          >
            <ArrowBackIcon sx={{ color: 'primary.main' }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'var(--gradient-primary, linear-gradient(135deg, #A8469B 0%, #569cd3 100%))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Goals Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all goals in the system
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search goals, teachers, edifiers..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">In Progress</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Goals List Table */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredGoals.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              No goals found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {searchValue || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No goals have been created yet'}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="goals table">
              <TableHead>
                <TableRow>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Edifier</TableCell>
                  <TableCell>Goal</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGoals.map((goal) => {
                  const user = goal.goalsFor;
                  const edifier = goal.goalsFrom;
                  
                  const userName = user?.name || 'Unknown User';
                  const userImage = user?.image || '';
                  const userRole = user?.role || '';
                  const userInitials = userName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);

                  const edifierName = edifier?.name || 'N/A';
                  const edifierImage = edifier?.image || '';
                  const edifierRole = edifier?.role || '';
                  const edifierInitials = edifierName !== 'N/A'
                    ? edifierName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)
                    : 'N/A';

                  return (
                    <TableRow
                      key={goal._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={userImage}
                            alt={userName}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'primary.main',
                            }}
                          >
                            {!userImage && userInitials}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.titles' }}>
                            {userName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {edifier ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={edifierImage}
                              alt={edifierName}
                              sx={{
                                width: 40,
                                height: 40,
                              }}
                            >
                              {!edifierImage && edifierInitials}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.titles' }}>
                              {edifierName}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {goal.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {goal.isCompleted ? (
                            <>
                              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                Completed
                              </Typography>
                            </>
                          ) : (
                            <>
                              <ScheduleIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                In Progress
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatDate(goal.timestamp)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
}

